import React, { useState } from 'react';

const SMSFlowDiagram = () => {
  const [activeStage, setActiveStage] = useState(null);

  const stages = [
    {
      id: 'submission',
      title: '1. SMS Submission',
      icon: '📱',
      color: '#3B82F6',
      description: 'User submits SMS through one of three channels',
      channels: [
        {
          name: 'GUI (Web Panel)',
          endpoint: 'POST /api/v1/send-sms',
          auth: 'JWT Bearer Token',
          flow: 'User fills form → Frontend sends JSON → Backend validates → Ingest',
          fields: 'to, message, entity_id, header_id, template_id, campaign_id',
        },
        {
          name: 'HTTP API (POST)',
          endpoint: 'POST /api/v1/sendsms',
          auth: 'X-API-KEY header',
          flow: 'External system sends POST → API Key validated → Ingest',
          fields: 'to, message, entity_id, header_id, template_id, pe_id, tm_id',
        },
        {
          name: 'HTTP API (GET)',
          endpoint: 'GET /api/v1/sms/send',
          auth: 'key= query param',
          flow: 'Traditional gateway format → Query params parsed → Ingest',
          fields: 'key, to, msg, entity_id, header_id, template_id',
        },
      ],
    },
    {
      id: 'validation',
      title: '2. Validation & DLT Check',
      icon: '✅',
      color: '#10B981',
      description: 'Backend validates the SMS before accepting it',
      checks: [
        { name: 'Authentication', detail: 'JWT token or API key validated against users table' },
        { name: 'Account Status', detail: 'User must be is_active=TRUE' },
        { name: 'Balance Check', detail: 'User balance checked from balances table (not users.balance)' },
        { name: 'DLT Entity', detail: 'entity_id must exist in entities table and be active' },
        { name: 'DLT Header', detail: 'header must be mapped to the entity and active' },
        { name: 'DLT Template', detail: 'template must belong to entity, message must match pattern' },
        { name: 'Mobile Validation', detail: '10-digit Indian mobile number (prefix 91 added if needed)' },
        { name: 'Keyword Blocking', detail: 'Message checked against keyword_block_rules' },
        { name: 'Mobile Blocking', detail: 'Destination checked against mobile_block_rules' },
      ],
    },
    {
      id: 'ingestion',
      title: '3. Ingestion',
      icon: '📥',
      color: '#F59E0B',
      description: 'Validated SMS written to the ingest queue for async processing',
      details: [
        { label: 'Service', value: 'SMSIngestService (internal/services/sms_ingest_service.go)' },
        { label: 'Queue Table', value: 'way2connect_worker.sms_ingest_requests' },
        { label: 'Write Mode', value: 'INSERT ONLY — no mutation, no enrichment' },
        { label: 'Batch Support', value: 'Up to 500 records per batch INSERT' },
        { label: 'Payload', value: 'Full JSON payload stored as payload_json column' },
        { label: 'Channel Tag', value: 'GUI, HTTP, or SMPP (source channel)' },
      ],
      table_schema: {
        name: 'sms_ingest_requests',
        columns: ['id', 'user_id', 'channel', 'payload_json', 'processed', 'processed_at', 'created_at'],
      },
    },
    {
      id: 'routing',
      title: '4. Routing Decision',
      icon: '🔀',
      color: '#8B5CF6',
      description: 'System determines HOW to deliver the SMS (HTTP or SMPP)',
      routing_chain: [
        {
          step: 1,
          name: 'Entity HTTP Routing',
          table: 'entity_http_routing',
          logic: 'Check if entity has a direct HTTP route configured',
          result: 'If found → delivery_type=HTTP, use this route',
        },
        {
          step: 2,
          name: 'Plan HTTP Mapping',
          table: 'plan_http_routes',
          logic: 'Check user\'s active plan for HTTP route mapping',
          result: 'If found → delivery_type=HTTP, use plan route',
        },
        {
          step: 3,
          name: 'Plan SMPP Mapping',
          table: 'plan_smpp_mappings',
          logic: 'Check user\'s active plan for SMPP connector mapping',
          result: 'If found → delivery_type=SMPP, use SMPP connector',
        },
        {
          step: 4,
          name: 'BLOCK',
          table: 'N/A',
          logic: 'No route found for this user/entity combination',
          result: 'SMS rejected with "No route available" error',
        },
      ],
      key_tables: ['user_plan_subscriptions', 'plans', 'plan_http_routes', 'plan_smpp_mappings', 'entity_http_routing'],
    },
    {
      id: 'submission-worker',
      title: '5. Worker Pickup & Submission',
      icon: '⚡',
      color: '#EF4444',
      description: 'Background workers pick up messages and deliver to operators',
      workers: [
        {
          name: 'HTTP Submit Worker',
          service: 'http-submit-worker.service',
          filter: 'delivery_type = "HTTP" or NULL/empty',
          process: [
            'Poll sms_ingest_requests for HTTP messages',
            'Resolve operator HTTP endpoint from routing',
            'Build HTTP POST request with operator credentials',
            'Send to operator API (e.g., api.operator.com/send)',
            'Parse response for message ID and status',
            'Write result to sms_reports',
          ],
        },
        {
          name: 'SMPP Worker',
          service: 'smpp-worker.service',
          filter: 'delivery_type = "SMPP"',
          process: [
            'Poll sms_ingest_requests for SMPP messages',
            'Resolve SMPP connector from plan mapping',
            'Build submit_sm PDU with DLT TLVs (0x1400, 0x1401, 0x1402)',
            'Auto-detect encoding: GSM7 for ASCII, UCS2 for Unicode',
            'Split long messages into segments with UDH headers',
            'Send via SMPP connection to operator',
            'Track sequence_number for async response',
            'Receive submit_sm_resp with operator message ID',
            'Write result to sms_reports with full operator_response JSON',
          ],
        },
      ],
    },
    {
      id: 'reporting',
      title: '6. Reporting & DLR',
      icon: '📊',
      color: '#06B6D4',
      description: 'Results stored in reporting database, DLR callbacks processed',
      details: [
        { label: 'Report Table', value: 'way2connect_reporting.sms_reports' },
        { label: 'Status Values', value: 'PENDING → SUBMITTED → SENT → DELIVERED / FAILED' },
        { label: 'Billing', value: 'Balance deducted on SUBMITTED, logged in sms_billing_log' },
        { label: 'DLR Callback', value: 'Operator sends delivery receipt → callback_dlr_logs' },
        { label: 'Operator Response', value: 'Full JSON stored in operator_response column' },
        { label: 'Retention', value: 'Reports kept indefinitely, partitioned by date' },
      ],
      report_fields: [
        'id', 'message_id', 'user_id', 'mobile_number', 'message', 'header_name',
        'entity_id', 'template_id', 'status', 'channel', 'operator_name',
        'operator_txn_id', 'operator_response', 'delivery_type', 'submitted_at',
        'delivered_at', 'cost', 'segments', 'encoding_type',
      ],
    },
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">SMS Delivery Flow</h1>
          <p className="mt-1 text-gray-600">End-to-end journey of an SMS from submission to delivery</p>
        </div>
        <div className="no-print flex gap-2">
          <button onClick={() => window.history.back()} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium">← Back</button>
          <button onClick={() => window.print()} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium">🖨️ Print</button>
        </div>
      </div>

      {/* Visual Flow Bar */}
      <div className="mb-8 bg-white rounded-xl border border-gray-200 p-4 overflow-x-auto">
        <div className="flex items-center justify-between min-w-[700px]">
          {stages.map((stage, i) => (
            <React.Fragment key={stage.id}>
              <button
                onClick={() => setActiveStage(activeStage === stage.id ? null : stage.id)}
                className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all cursor-pointer ${activeStage === stage.id ? 'bg-blue-50 ring-2 ring-blue-300' : 'hover:bg-gray-50'}`}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-lg" style={{ backgroundColor: `${stage.color}15` }}>
                  {stage.icon}
                </div>
                <span className="text-xs font-medium text-gray-700 text-center whitespace-nowrap">{stage.title.split('. ')[1]}</span>
              </button>
              {i < stages.length - 1 && (
                <div className="flex-1 h-0.5 bg-gray-200 mx-1 min-w-[20px]" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Stage Details */}
      {stages.map((stage, idx) => (
        <div key={stage.id} className={`mb-6 bg-white rounded-xl border border-gray-200 overflow-hidden ${idx > 2 ? 'print-break' : ''}`} style={{ borderLeft: `4px solid ${stage.color}` }}>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">{stage.icon}</span>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{stage.title}</h2>
                <p className="text-sm text-gray-500">{stage.description}</p>
              </div>
            </div>

            {/* Stage 1: Submission Channels */}
            {stage.channels && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {stage.channels.map((ch, i) => (
                  <div key={i} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">{ch.name}</h3>
                    <div className="space-y-2 text-sm">
                      <div><span className="text-gray-500">Endpoint:</span> <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{ch.endpoint}</code></div>
                      <div><span className="text-gray-500">Auth:</span> <span className="text-gray-700">{ch.auth}</span></div>
                      <div><span className="text-gray-500">Flow:</span> <span className="text-gray-600 text-xs">{ch.flow}</span></div>
                      <div><span className="text-gray-500">Fields:</span> <span className="text-gray-600 text-xs">{ch.fields}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Stage 2: Validation Checks */}
            {stage.checks && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {stage.checks.map((check, i) => (
                  <div key={i} className="flex items-start gap-2 bg-green-50 rounded-lg p-3">
                    <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                    <div>
                      <div className="text-sm font-medium text-gray-800">{check.name}</div>
                      <div className="text-xs text-gray-500">{check.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Stage 3: Ingestion Details */}
            {stage.details && !stage.report_fields && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {stage.details.map((d, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <span className="font-medium text-gray-700 min-w-[100px]">{d.label}:</span>
                    <span className="text-gray-600">{d.value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Stage 3: Table Schema */}
            {stage.table_schema && (
              <div className="mt-4 bg-amber-50 rounded-lg p-3">
                <div className="text-xs font-semibold text-amber-700 mb-1">Table: {stage.table_schema.name}</div>
                <div className="flex flex-wrap gap-1">
                  {stage.table_schema.columns.map((col, i) => (
                    <code key={i} className="text-xs px-2 py-0.5 bg-white text-amber-800 rounded border border-amber-200">{col}</code>
                  ))}
                </div>
              </div>
            )}

            {/* Stage 4: Routing Chain */}
            {stage.routing_chain && (
              <div className="space-y-3">
                {stage.routing_chain.map((r, i) => (
                  <div key={i} className="flex items-start gap-4 bg-purple-50 rounded-lg p-4">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ backgroundColor: i < 3 ? '#8B5CF6' : '#EF4444' }}>
                      {r.step}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{r.name}</div>
                      <div className="text-sm text-gray-600">{r.logic}</div>
                      <div className="text-xs text-purple-600 mt-1">→ {r.result}</div>
                      {r.table !== 'N/A' && <code className="text-xs text-gray-500">Table: {r.table}</code>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Stage 5: Workers */}
            {stage.workers && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {stage.workers.map((w, i) => (
                  <div key={i} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900">{w.name}</h3>
                    <div className="text-xs text-gray-500 mb-3">Service: {w.service} | Filter: {w.filter}</div>
                    <ol className="space-y-1.5">
                      {w.process.map((step, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                          <span className="w-5 h-5 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs font-bold flex-shrink-0">{j + 1}</span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}
              </div>
            )}

            {/* Stage 6: Reporting */}
            {stage.report_fields && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  {stage.details.map((d, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <span className="font-medium text-gray-700 min-w-[120px]">{d.label}:</span>
                      <span className="text-gray-600">{d.value}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-cyan-50 rounded-lg p-3">
                  <div className="text-xs font-semibold text-cyan-700 mb-2">Key Report Fields (sms_reports)</div>
                  <div className="flex flex-wrap gap-1">
                    {stage.report_fields.map((f, i) => (
                      <code key={i} className="text-xs px-2 py-0.5 bg-white text-cyan-800 rounded border border-cyan-200">{f}</code>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SMSFlowDiagram;
